import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { acknowledgeAmpyConversation, getAmpyChatContext, getOrCreateAmpyChatContext, runAmpyConversation } from 'Apis/library';
import type { AnalyticsChatMessage } from 'Components/pages/AnalyticsDashboardPage/AnalyticsAmpyChatPanel';

const AMPY_CHAT_STORAGE_KEY = 'ampy_chat_context_v1';
const AMPY_CHAT_OPEN_KEY = 'ampy_chat_sidebar_open';
const AMPY_CHAT_UNAVAILABLE_UNTIL_KEY = 'ampy_chat_unavailable_until';
const AMPY_CHAT_DEBUG_ENABLED = process.env.REACT_APP_AMPY_CHAT_DEBUG === 'true';
const AMPY_CHAT_INIT_TIMEOUT_MS = 10000;
const AMPY_CHAT_UNAVAILABLE_COOLDOWN_S = Number(process.env.REACT_APP_AMPY_CHAT_UNAVAILABLE_COOLDOWN_S ?? '120') || 120;
const AMPY_CHAT_UNAVAILABLE_COOLDOWN_MS = Math.max(0, AMPY_CHAT_UNAVAILABLE_COOLDOWN_S * 1000);
const AMPY_CHAT_TIMEOUT_ERROR = `Ampy chat context request timed out after ${AMPY_CHAT_INIT_TIMEOUT_MS}ms.`;
const AMPY_CHAT_UNAVAILABLE_MESSAGE = 'Ask Ampy is currently unavailable, please try again later.';
const AMPY_CHAT_DEFAULT_ACKNOWLEDGEMENT = "I'll work through that request and share the result shortly.";

type AmpyChatContextType = {
    messages: AnalyticsChatMessage[];
    pending: boolean;
    pendingAcknowledgement: string | null;
    error: string | null;
    isOpen: boolean;
    isContextRefreshDisabled: boolean;
    isUnavailable: boolean;
    sendMessage: (prompt: string) => Promise<void> | void;
    clearChat: () => void;
    toggleOpen: () => void;
    setOpen: (open: boolean) => void;
};

const AmpyChatContext = createContext<AmpyChatContextType>({
    messages: [],
    pending: false,
    pendingAcknowledgement: null,
    error: null,
    isOpen: false,
    isContextRefreshDisabled: false,
    isUnavailable: false,
    sendMessage: () => {},
    clearChat: () => {},
    toggleOpen: () => {},
    setOpen: () => {},
});

export const useAmpyChat = () => useContext(AmpyChatContext);

export const AmpyChatProvider = ({ children }: { children: React.ReactNode }) => {
    const [messages, setMessages] = useState<AnalyticsChatMessage[]>([]);
    const [contextId, setContextId] = useState<string | null>(null);
    const [initializing, setInitializing] = useState(false);
    const [pending, setPending] = useState(false);
    const [pendingAcknowledgement, setPendingAcknowledgement] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isUnavailable, setIsUnavailable] = useState(false);
    const [requiresManualRetry, setRequiresManualRetry] = useState(false);
    const [isOpen, setIsOpen] = useState(() => {
        try {
            return window.localStorage.getItem(AMPY_CHAT_OPEN_KEY) === 'true';
        } catch {
            return false;
        }
    });

    const initRequestIdRef = useRef(0);
    const sendRequestIdRef = useRef(0);
    const isInitializingRef = useRef(false);
    const hasInitializedContextRef = useRef(false);
    const debugCountersRef = useRef<Record<string, number>>({});
    const unavailableResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const debugLog = useCallback((event: string, metadata: Record<string, unknown> = {}) => {
        if (!AMPY_CHAT_DEBUG_ENABLED) {
            return;
        }

        const nextCount = (debugCountersRef.current[event] ?? 0) + 1;
        debugCountersRef.current = { ...debugCountersRef.current, [event]: nextCount };

        (window as Window & { __AMPY_CHAT_DEBUG__?: unknown }).__AMPY_CHAT_DEBUG__ = {
            counters: debugCountersRef.current,
            lastEvent: event,
            lastMetadata: metadata,
            timestamp: new Date().toISOString(),
        };

        // eslint-disable-next-line no-console
        console.info('[AmpyChatDebug]', event, {
            ...metadata,
            counters: debugCountersRef.current,
        });
    }, []);

    const getStoredContextId = useCallback((): string | undefined => {
        try {
            const raw = window.localStorage.getItem(AMPY_CHAT_STORAGE_KEY);
            if (!raw) {
                return undefined;
            }
            const parsed = JSON.parse(raw) as { contextId?: unknown };
            return typeof parsed.contextId === 'string' && parsed.contextId.trim() ? parsed.contextId : undefined;
        } catch {
            return undefined;
        }
    }, []);

    const removeStoredContextId = useCallback(() => {
        try {
            window.localStorage.removeItem(AMPY_CHAT_STORAGE_KEY);
        } catch {
            // ignore storage errors
        }
    }, []);

    const persistContextId = useCallback((nextContextId: string) => {
        try {
            window.localStorage.setItem(AMPY_CHAT_STORAGE_KEY, JSON.stringify({ contextId: nextContextId }));
        } catch {
            // ignore storage errors
        }
    }, []);

    const clearUnavailableCooldown = useCallback(() => {
        if (unavailableResetTimerRef.current) {
            clearTimeout(unavailableResetTimerRef.current);
            unavailableResetTimerRef.current = null;
        }
        try {
            window.localStorage.removeItem(AMPY_CHAT_UNAVAILABLE_UNTIL_KEY);
        } catch {
            // ignore storage errors
        }
    }, []);

    const scheduleUnavailableReset = useCallback((remainingMs: number) => {
        if (unavailableResetTimerRef.current) {
            clearTimeout(unavailableResetTimerRef.current);
        }
        unavailableResetTimerRef.current = setTimeout(() => {
            setIsUnavailable(false);
            clearUnavailableCooldown();
        }, Math.max(0, remainingMs));
    }, [clearUnavailableCooldown]);

    const getUnavailableUntil = useCallback((): number | null => {
        try {
            const raw = window.localStorage.getItem(AMPY_CHAT_UNAVAILABLE_UNTIL_KEY);
            if (!raw) {
                return null;
            }
            const parsed = Number(raw);
            if (!Number.isFinite(parsed) || parsed <= 0) {
                return null;
            }
            return parsed;
        } catch {
            return null;
        }
    }, []);

    const markUnavailableWithCooldown = useCallback(() => {
        const unavailableUntil = Date.now() + AMPY_CHAT_UNAVAILABLE_COOLDOWN_MS;
        try {
            window.localStorage.setItem(AMPY_CHAT_UNAVAILABLE_UNTIL_KEY, String(unavailableUntil));
        } catch {
            // ignore storage errors
        }
        setIsUnavailable(true);
        scheduleUnavailableReset(AMPY_CHAT_UNAVAILABLE_COOLDOWN_MS);
    }, [scheduleUnavailableReset]);

    const withInitTimeout = useCallback(async <T,>(promise: Promise<T>): Promise<T> => {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        const timeoutPromise = new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => reject(new Error(AMPY_CHAT_TIMEOUT_ERROR)), AMPY_CHAT_INIT_TIMEOUT_MS);
        });
        try {
            return await Promise.race([promise, timeoutPromise]);
        } finally {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }
    }, []);

    const isInitTimeoutError = useCallback((err: unknown) => {
        return err instanceof Error && err.message === AMPY_CHAT_TIMEOUT_ERROR;
    }, []);

    const initializeContext = useCallback(async (force = false) => {
        if (!isOpen) {
            debugLog('init_skipped_sidebar_closed', { force });
            return;
        }
        if (isUnavailable) {
            debugLog('init_skipped_unavailable', { force });
            return;
        }
        if (!force && requiresManualRetry) {
            debugLog('init_skipped_waiting_for_manual_retry', { force });
            return;
        }
        if (!force && hasInitializedContextRef.current) {
            debugLog('init_skipped_already_initialized', { force, contextId });
            return;
        }
        if (isInitializingRef.current) {
            debugLog('init_skipped_inflight', { force });
            return;
        }

        const requestId = initRequestIdRef.current + 1;
        initRequestIdRef.current = requestId;
        isInitializingRef.current = true;
        debugLog('init_started', { requestId, force });

        setInitializing(true);
        try {
            const storedContextId = getStoredContextId();
            let response;
            if (storedContextId) {
                debugLog('init_fetch_existing_attempt', { requestId, storedContextId });
                try {
                    response = await withInitTimeout(getAmpyChatContext(storedContextId));
                    debugLog('init_fetch_existing_success', { requestId, storedContextId });
                } catch (err) {
                    if (isInitTimeoutError(err)) {
                        throw err;
                    }
                    debugLog('init_fetch_existing_failed_fallback_create', { requestId, storedContextId });
                    removeStoredContextId();
                    debugLog('init_create_attempt', { requestId, source: 'fallback_after_get_failure' });
                    response = await withInitTimeout(getOrCreateAmpyChatContext());
                    debugLog('init_create_success', { requestId, source: 'fallback_after_get_failure' });
                }
            } else {
                debugLog('init_create_attempt', { requestId, source: 'no_stored_context' });
                response = await withInitTimeout(getOrCreateAmpyChatContext());
                debugLog('init_create_success', { requestId, source: 'no_stored_context' });
            }

            if (initRequestIdRef.current !== requestId) {
                debugLog('init_result_ignored_outdated_request', { requestId, latestRequestId: initRequestIdRef.current });
                return;
            }

            setContextId(response.contextId);
            setMessages(
                (response.messages ?? []).map((msg) => ({
                    id: msg.id,
                    sender: msg.sender,
                    message: msg.message,
                })),
            );
            persistContextId(response.contextId);
            hasInitializedContextRef.current = true;
            setIsUnavailable(false);
            setRequiresManualRetry(false);
            clearUnavailableCooldown();
            setError(null);
            debugLog('init_completed', {
                requestId,
                resolvedContextId: response.contextId,
                restoredMessageCount: response.messages?.length ?? 0,
            });
        } catch (err) {
            if (initRequestIdRef.current !== requestId) {
                debugLog('init_error_ignored_outdated_request', { requestId, latestRequestId: initRequestIdRef.current });
                return;
            }
            hasInitializedContextRef.current = false;
            if (isInitTimeoutError(err)) {
                initRequestIdRef.current = requestId + 1;
                markUnavailableWithCooldown();
                setPending(false);
                setInitializing(false);
                isInitializingRef.current = false;
                setRequiresManualRetry(true);
                setError(AMPY_CHAT_UNAVAILABLE_MESSAGE);
                setContextId(null);
                debugLog('init_timeout_unavailable', { requestId });
                return;
            }
            setError(err instanceof Error ? err.message : 'Unable to load chat context.');
            setMessages([]);
            setContextId(null);
            debugLog('init_failed', {
                requestId,
                error: err instanceof Error ? err.message : 'unknown_error',
            });
        } finally {
            if (initRequestIdRef.current === requestId) {
                setInitializing(false);
            }
            isInitializingRef.current = false;
            debugLog('init_finished', { requestId, activeRequestId: initRequestIdRef.current });
        }
    }, [clearUnavailableCooldown, contextId, debugLog, getStoredContextId, isInitTimeoutError, isOpen, isUnavailable, markUnavailableWithCooldown, persistContextId, removeStoredContextId, requiresManualRetry, withInitTimeout]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        void initializeContext();
    }, [initializeContext, isOpen]);

    useEffect(() => {
        const unavailableUntil = getUnavailableUntil();
        if (!unavailableUntil) {
            if (isUnavailable) {
                setIsUnavailable(false);
            }
            clearUnavailableCooldown();
            return;
        }
        const remainingMs = unavailableUntil - Date.now();
        if (remainingMs <= 0) {
            setIsUnavailable(false);
            clearUnavailableCooldown();
            return;
        }
        setIsUnavailable(true);
        scheduleUnavailableReset(remainingMs);
    }, [clearUnavailableCooldown, getUnavailableUntil, isUnavailable, scheduleUnavailableReset]);

    useEffect(() => {
        if (!isUnavailable) {
            return;
        }
        const intervalId = setInterval(() => {
            const unavailableUntil = getUnavailableUntil();
            if (!unavailableUntil || unavailableUntil <= Date.now()) {
                setIsUnavailable(false);
                clearUnavailableCooldown();
            }
        }, 1000);
        return () => {
            clearInterval(intervalId);
        };
    }, [clearUnavailableCooldown, getUnavailableUntil, isUnavailable]);

    useEffect(() => {
        return () => {
            if (unavailableResetTimerRef.current) {
                clearTimeout(unavailableResetTimerRef.current);
            }
        };
    }, []);

    const shouldShowPending = pending || (initializing && !isUnavailable);
    const isContextRefreshDisabled = isUnavailable || (initializing && !isUnavailable);

    useEffect(() => {
        if (!contextId) {
            return;
        }
        try {
            persistContextId(contextId);
        } catch {
            // ignore storage errors
        }
    }, [contextId, persistContextId]);

    const sendMessage = useCallback(
        async (prompt: string) => {
            if (!contextId) {
                debugLog('send_skipped_missing_context', { promptLength: prompt.length, isOpen });
                setError('Chat context is still loading. Please try again in a moment.');
                return;
            }
            if (isUnavailable) {
                setError(AMPY_CHAT_UNAVAILABLE_MESSAGE);
                return;
            }
            if (!isOpen) {
                debugLog('send_skipped_sidebar_closed', { contextId, promptLength: prompt.length });
                return;
            }
            const userMessage: AnalyticsChatMessage = {
                id: `user-${Date.now()}`,
                sender: 'user',
                message: prompt,
            };
            const sendRequestId = sendRequestIdRef.current + 1;
            sendRequestIdRef.current = sendRequestId;
            setMessages((prev) => [...prev, userMessage]);
            setPending(true);
            setError(null);
            setPendingAcknowledgement(AMPY_CHAT_DEFAULT_ACKNOWLEDGEMENT);
            debugLog('send_started', { contextId, promptLength: prompt.length });
            void acknowledgeAmpyConversation({
                message: prompt,
                currentPage: window.location.pathname,
            })
                .then((response) => {
                    if (sendRequestIdRef.current !== sendRequestId) {
                        return;
                    }
                    const acknowledgement = response?.acknowledgement?.trim();
                    setPendingAcknowledgement(acknowledgement || AMPY_CHAT_DEFAULT_ACKNOWLEDGEMENT);
                    debugLog('send_ack_completed', { contextId, hasAcknowledgement: Boolean(acknowledgement) });
                })
                .catch(() => {
                    if (sendRequestIdRef.current !== sendRequestId) {
                        return;
                    }
                    setPendingAcknowledgement(AMPY_CHAT_DEFAULT_ACKNOWLEDGEMENT);
                    debugLog('send_ack_failed', { contextId });
                });
            try {
                const response = await runAmpyConversation({ message: prompt, contextId, currentPage: window.location.pathname });
                if (sendRequestIdRef.current !== sendRequestId) {
                    return;
                }
                setContextId(response.contextId);
                setMessages((prev) => [
                    ...prev,
                    { id: `ampy-${Date.now()}`, sender: 'ampy', message: response.answer },
                ]);
                debugLog('send_completed', { contextId: response.contextId });
            } catch (err) {
                if (sendRequestIdRef.current !== sendRequestId) {
                    return;
                }
                const msg = err instanceof Error ? err.message : 'Unable to fetch response.';
                setError(msg);
                setMessages((prev) => [
                    ...prev,
                    { id: `ampy-error-${Date.now()}`, sender: 'ampy', message: `I couldn't run that request. ${msg}` },
                ]);
                debugLog('send_failed', { contextId, error: msg });
            } finally {
                if (sendRequestIdRef.current === sendRequestId) {
                    setPending(false);
                    setPendingAcknowledgement(null);
                    sendRequestIdRef.current = sendRequestId + 1;
                }
                debugLog('send_finished', { contextId });
            }
        },
        [contextId, debugLog, isOpen, isUnavailable],
    );

    const clearChat = useCallback(() => {
        if (isUnavailable || initializing) {
            debugLog('clear_chat_blocked', { isUnavailable, initializing });
            return;
        }
        debugLog('clear_chat_requested', { isOpen });
        removeStoredContextId();
        hasInitializedContextRef.current = false;
        setRequiresManualRetry(false);
        setContextId(null);
        setMessages([]);
        sendRequestIdRef.current += 1;
        setPending(false);
        setPendingAcknowledgement(null);
        setError(null);
        if (isOpen) {
            debugLog('clear_chat_reinitialize', { force: true });
            void initializeContext(true);
        }
    }, [debugLog, initializeContext, initializing, isOpen, isUnavailable, removeStoredContextId]);

    const toggleOpen = useCallback(() => {
        setIsOpen((prev) => {
            const next = !prev;
            try {
                window.localStorage.setItem(AMPY_CHAT_OPEN_KEY, String(next));
            } catch {
                // ignore
            }
            debugLog('sidebar_toggled', { previous: prev, next });
            return next;
        });
    }, [debugLog]);

    const setOpen = useCallback((open: boolean) => {
        setIsOpen(open);
        try {
            window.localStorage.setItem(AMPY_CHAT_OPEN_KEY, String(open));
        } catch {
            // ignore
        }
        debugLog('sidebar_set_open', { open });
    }, [debugLog]);

    return (
        <AmpyChatContext.Provider
            value={{
                messages,
                pending: shouldShowPending,
                pendingAcknowledgement,
                error,
                isOpen,
                isContextRefreshDisabled,
                isUnavailable,
                sendMessage,
                clearChat,
                toggleOpen,
                setOpen,
            }}
        >
            {children}
        </AmpyChatContext.Provider>
    );
};

export default AmpyChatContext;
